import { SingleDropdown } from "@/components/molecules/SingleDropdown/SingleDropdown";
import {
  isValidWeatherParameterFieldDisplayName,
  weatherParameterFieldDisplayName,
} from "@/i18n/weather-parameter-fields";
import type { LayerKind } from "@/layers/utility/createLayerObject";
import type { ParameterChangeCallback } from "@/overlay/components/WeatherParameterForm/index";
import { weatherParamOptionFilter } from "@/overlay/components/WeatherParameterForm/optionFilter";
import { FormField } from "@/overlay/components/form-field";
import type { ParameterRange } from "@mm/api-layers.meteomatics.com";
import Logger from "@mm/logging";
import type {
  AllFieldNames,
  FieldNames,
  HasLayerIdxNarrowing,
  PartialWeatherParameter,
} from "@mm/weather-parameter-utils";
import * as weatherParameter from "@mm/weather-parameter-utils";
import { useEffect, useMemo, useState } from "react";

const logger = Logger.fromFilename(__filename);

function isRangeLike(value: unknown): value is ParameterRange {
  return (
    !!value && typeof value === "object" && "start" in value && typeof (value as ParameterRange).start === "number"
  );
}

function isStringArrayLike(value: unknown): value is string[] {
  return Array.isArray(value) && (value.length === 0 || typeof value[0] === "string");
}

/**
 * Renders a single layer of a `PartialWeatherParameter` as a form, which allows a user to modify the fields
 * of the weather parameter.
 *
 * Does not allow modification of the parameter format, resp. selected layer. Use `WeatherParameterForm` for these cases.
 */
// TODO: factor this into some higher oder input components: SelectInputField, NumberInputField, make suffix and prefix a property, make hint a property or composable Component etc
export function ParameterFormatForm(props: {
  parameter: HasLayerIdxNarrowing<PartialWeatherParameter>;
  layerKind?: LayerKind;
  onChange: ParameterChangeCallback;
}) {
  const MAGIC_VALUE_NONE_SELECTED = "__none_selected";
  const _layer = props.parameter.parameter.layers[props.parameter.narrowed_selection.layerIdx];
  const layer = weatherParamOptionFilter(_layer, props.layerKind);

  // TODO: this should be possible without the type assertions, maybe do not assert at all and use parametername as fallback
  // This could actually occur if the backend is updated without reloading the frontend.
  const allFields: [AllFieldNames, string[] | string | ParameterRange | ParameterRange[]][] = Object.entries(
    layer,
  ) as unknown as any;
  const fields: [FieldNames, string[] | ParameterRange | ParameterRange[]][] = allFields.filter(
    ([fieldName]) => fieldName !== "format",
  ) as unknown as any;

  function clampRangeValue(range: ParameterRange, value: number) {
    return Math.min(range.stop, Math.max(range.start, value));
  }

  function getFieldRanges(fieldDesc: ParameterRange | ParameterRange[] | string[]) {
    if (isRangeLike(fieldDesc)) {
      return [fieldDesc];
    }

    if (Array.isArray(fieldDesc) && (fieldDesc.length === 0 || isRangeLike(fieldDesc[0]))) {
      return fieldDesc as ParameterRange[];
    }

    return [];
  }

  function getActiveRange(ranges: ParameterRange[], value: string | undefined): ParameterRange {
    if (value) {
      const matchingRange = ranges.find((range) => weatherParameter.parseParameterRange(range, value) != null);
      if (matchingRange) {
        return matchingRange;
      }
    }

    return ranges[0];
  }

  function getRangeNumericValue(ranges: ParameterRange[], value: string | undefined): number | null {
    if (!value) {
      return null;
    }

    for (const range of ranges) {
      const parsed = weatherParameter.parseParameterRange(range, value);
      if (parsed != null) {
        return parsed;
      }
    }

    return null;
  }

  function changeRangeParameterUnit(field: FieldNames, ranges: ParameterRange[], unit: string) {
    const newParam: HasLayerIdxNarrowing<PartialWeatherParameter> = weatherParameter.copy(props.parameter);
    const nextRange = ranges.find((range) => range.unit === unit);
    if (!nextRange) {
      return;
    }

    const currentValue = newParam.narrowed_selection.fields[field];
    const nextValue = clampRangeValue(nextRange, getRangeNumericValue(ranges, currentValue) ?? nextRange.start);
    newParam.narrowed_selection.fields[field] = weatherParameter.formatParameterRange(nextRange, nextValue);
    props.onChange(newParam);
  }

  function changeParameterValue(
    field: FieldNames,
    fieldDesc: ParameterRange | ParameterRange[] | string[],
    newValue: string,
    preferredRangeUnit?: string,
  ) {
    const newParam: HasLayerIdxNarrowing<PartialWeatherParameter> = weatherParameter.copy(props.parameter);
    const ranges = getFieldRanges(fieldDesc);

    if (ranges.length > 0) {
      if (newValue === "") {
        delete newParam.narrowed_selection.fields[field];
      } else {
        const currentValue = newParam.narrowed_selection.fields[field];
        const activeRange =
          ranges.find((range) => range.unit === preferredRangeUnit) ?? getActiveRange(ranges, currentValue);
        newParam.narrowed_selection.fields[field] = weatherParameter.formatParameterRange(
          activeRange,
          clampRangeValue(activeRange, Math.abs(+newValue)),
        );
      }
    } else if (isStringArrayLike(fieldDesc)) {
      newParam.narrowed_selection.fields[field] = newValue;
    }

    props.onChange(newParam);
  }

  function rangeParameterValue(ranges: ParameterRange[], value: string | undefined): string {
    const parsed = getRangeNumericValue(ranges, value);
    if (parsed != null) {
      return `${parsed}`;
    }

    if (value) {
      logger.error("failed to parse range paramter value <", value, "> for", ranges);
    }

    return "";
  }

  return (
    <>
      {fields.map(([fieldName, fieldDesc], fieldIdx) => {
        if (!isValidWeatherParameterFieldDisplayName(fieldName)) {
          console.warn("Something wrong in weather parameter form: ", fieldName);
          return null;
        }
        const ranges = getFieldRanges(fieldDesc);
        if (ranges.length > 0) {
          return (
            <RangeField
              key={`${fieldName}-${fieldIdx}-${props.parameter.narrowed_selection.layerIdx}`}
              currentValue={props.parameter.narrowed_selection.fields[fieldName]}
              fieldDesc={fieldDesc}
              fieldName={fieldName}
              onChange={changeParameterValue}
              onUnitChange={changeRangeParameterUnit}
              rangeParameterValue={rangeParameterValue}
              ranges={ranges}
            />
          );
        }
        if (!isStringArrayLike(fieldDesc)) {
          return null;
        }
        // biome-ignore lint/correctness/useJsxKeyInIterable:Key is added
        return fieldDesc.length <= 0 ? null : (
          <FormField key={fieldName} label={weatherParameterFieldDisplayName[fieldName]()}>
            <SingleDropdown
              options={fieldDesc.map((val: string) => ({ value: val, label: val }))}
              onChange={(newValue) => changeParameterValue(fieldName, fieldDesc, newValue)}
              value={props.parameter.narrowed_selection.fields[fieldName] ?? MAGIC_VALUE_NONE_SELECTED}
            />
          </FormField>
        );
      })}
    </>
  );
}

function RangeField(props: {
  currentValue: string | undefined;
  fieldDesc: ParameterRange | ParameterRange[] | string[];
  fieldName: FieldNames;
  onChange: (
    field: FieldNames,
    fieldDesc: ParameterRange | ParameterRange[] | string[],
    newValue: string,
    preferredRangeUnit?: string,
  ) => void;
  onUnitChange: (field: FieldNames, ranges: ParameterRange[], unit: string) => void;
  rangeParameterValue: (ranges: ParameterRange[], value: string | undefined) => string;
  ranges: ParameterRange[];
}) {
  const { currentValue, fieldDesc, fieldName, onChange, onUnitChange, rangeParameterValue, ranges } = props;
  const activeRangeFromValue = useMemo(() => {
    if (currentValue) {
      const matchingRange = ranges.find((range) => weatherParameter.parseParameterRange(range, currentValue) != null);
      if (matchingRange) {
        return matchingRange;
      }
    }
    return ranges[0];
  }, [currentValue, ranges]);
  const [selectedUnit, setSelectedUnit] = useState(activeRangeFromValue.unit);

  useEffect(() => {
    if (currentValue) {
      setSelectedUnit(activeRangeFromValue.unit);
    }
  }, [activeRangeFromValue.unit, currentValue]);

  const activeRange = useMemo(
    () => ranges.find((range) => range.unit === selectedUnit) ?? activeRangeFromValue,
    [activeRangeFromValue, ranges, selectedUnit],
  );

  return (
    <FormField label={weatherParameterFieldDisplayName[fieldName]()}>
      <div className="grid grid--center-horizontal" data-columns="1,flex">
        <input
          type="number"
          min={activeRange.start}
          max={activeRange.stop}
          value={rangeParameterValue(ranges, currentValue)}
          onChange={(e) => onChange(fieldName, fieldDesc, e.target.value, activeRange.unit)}
        />
        {ranges.length > 1 ? (
          <SingleDropdown
            options={ranges
              .filter((range): range is ParameterRange & { unit: string } => typeof range.unit === "string")
              .map((range) => ({ value: range.unit, label: range.unit }))}
            value={activeRange.unit}
            onChange={(value) => {
              setSelectedUnit(value);
              onUnitChange(fieldName, ranges, value);
            }}
          />
        ) : (
          <div>{activeRange.unit}</div>
        )}
      </div>
    </FormField>
  );
}
